const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/api`;
    this.authToken = null;
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Auto-recover from expired/invalid JWT — clear the bad token and
      // redirect to login so admin doesn't get stuck on a blank page.
      const detail = (errorData.detail || '').toString().toLowerCase();
      const isExpired =
        response.status === 401 ||
        detail.includes('invalid or expired') ||
        detail.includes('could not validate') ||
        detail.includes('token has expired');

      if (isExpired && this.authToken) {
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        } catch (e) {
          // localStorage may be unavailable (SSR / private mode) — ignore
        }
        this.authToken = null;
        // Notify the rest of the app (AuthContext, CartContext, etc.)
        // so in-memory state clears too and re-renders as a guest.
        if (typeof window !== 'undefined') {
          try {
            window.dispatchEvent(new CustomEvent('auth:expired'));
          } catch (e) {
            // ignore
          }
          // Only redirect when the user is currently in an admin/account page.
          // Public pages keep working anonymously.
          const path = window.location.pathname || '';
          if (path.startsWith('/admin') || path.startsWith('/account')) {
            const next = encodeURIComponent(path + window.location.search);
            window.location.href = `/login?session=expired&next=${next}`;
          }
        }
      }

      // Create error with status and detail for proper error handling
      const error = new Error(errorData.detail || `Request failed with status ${response.status}`);
      error.status = response.status;
      error.statusCode = response.status;
      error.detail = errorData.detail;
      throw error;
    }

    return response.json();
  }

  // Auth endpoints
  async register(firstName, lastName, email, password) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email,
        password
      })
    });
  }

  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async verifyOtp(email, otp) {
    return this.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp })
    });
  }

  async resendOtp(email) {
    return this.request('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async updateProfile(data) {
    return this.request('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async requestPasswordReset(email) {
    return this.request('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async confirmPasswordReset(token, newPassword) {
    return this.request('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword })
    });
  }

  // Wishlist endpoints
  async getWishlist() {
    return this.request('/wishlist');
  }

  async addToWishlist(productId) {
    return this.request('/wishlist/items', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId })
    });
  }

  async removeFromWishlist(productId) {
    return this.request(`/wishlist/items/${productId}`, {
      method: 'DELETE'
    });
  }

  async clearWishlist() {
    return this.request('/wishlist', {
      method: 'DELETE'
    });
  }

  async checkInWishlist(productId) {
    return this.request(`/wishlist/check/${productId}`);
  }

  // Product endpoints
  async getProducts(params = {}) {
    const queryString = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryString.append(key, value);
      }
    });

    const query = queryString.toString();
    return this.request(`/products${query ? `?${query}` : ''}`);
  }

  async getProduct(productId) {
    return this.request(`/products/${productId}`);
  }

  async getProductsByCategory(category) {
    return this.request(`/products/category/${category}`);
  }

  async getSearchSuggestions(query) {
    return this.request(`/products/suggestions?q=${encodeURIComponent(query)}`);
  }

  async searchProducts(query, filters = {}) {
    return this.getProducts({ q: query, ...filters });
  }

  // Cart endpoints
  async getCart() {
    return this.request('/cart');
  }

  async addToCart(productId, quantity = 1, variantId = null) {
    const body = { product_id: productId, quantity };
    if (variantId) {
      body.variant_id = variantId;
    }
    return this.request('/cart/items', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async updateCartItem(productId, quantity) {
    return this.request(`/cart/items/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity })
    });
  }

  async removeFromCart(productId) {
    return this.request(`/cart/items/${productId}`, {
      method: 'DELETE'
    });
  }

  async clearCart() {
    return this.request('/cart', {
      method: 'DELETE'
    });
  }

  async syncCart(items) {
    return this.request('/cart/sync', {
      method: 'POST',
      body: JSON.stringify(items)
    });
  }

  // Recommendations endpoints
  async trackRecentlyViewed(productId) {
    return this.request('/recommendations/recently-viewed', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId })
    });
  }

  async getRecentlyViewed(limit = 10) {
    return this.request(`/recommendations/recently-viewed?limit=${limit}`);
  }

  async getPersonalizedRecommendations() {
    return this.request('/recommendations/personalized');
  }

  // Reviews endpoints
  async getProductReviews(productId, sortBy = 'recent', page = 1, perPage = 10) {
    return this.request(`/reviews/${productId}?sort_by=${sortBy}&page=${page}&per_page=${perPage}`);
  }

  async submitReview(productId, rating, title, content) {
    return this.request('/reviews', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, rating, title, content })
    });
  }

  async markReviewHelpful(reviewId, helpful) {
    return this.request(`/reviews/${reviewId}/helpful`, {
      method: 'POST',
      body: JSON.stringify({ helpful })
    });
  }

  async deleteReview(reviewId) {
    return this.request(`/reviews/${reviewId}`, {
      method: 'DELETE'
    });
  }

  // Admin endpoints
  async getAdminOverview() {
    return this.request('/admin/stats/overview');
  }

  async getAdminProductStats() {
    return this.request('/admin/stats/products');
  }

  async getAdminCustomerStats() {
    return this.request('/admin/stats/customers');
  }

  async getAdminProducts(params = {}) {
    // Filter out undefined/null values to prevent sending "undefined" as string
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryString = new URLSearchParams(filteredParams).toString();
    return this.request(`/admin/products${queryString ? `?${queryString}` : ''}`);
  }

  async createAdminProduct(product) {
    return this.request('/admin/products', {
      method: 'POST',
      body: JSON.stringify(product)
    });
  }

  async updateAdminProduct(productId, updates) {
    return this.request(`/admin/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteAdminProduct(productId) {
    return this.request(`/admin/products/${productId}`, {
      method: 'DELETE'
    });
  }

  async getAdminCustomers(params = {}) {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryString = new URLSearchParams(filteredParams).toString();
    return this.request(`/admin/customers${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminCustomerDetails(customerId) {
    return this.request(`/admin/customers/${customerId}`);
  }

  async getAdminReviews(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/reviews${queryString ? `?${queryString}` : ''}`);
  }

  async adminDeleteReview(reviewId) {
    return this.request(`/admin/reviews/${reviewId}`, {
      method: 'DELETE'
    });
  }

  async updateProductStock(productId, inStock) {
    return this.request(`/admin/products/${productId}/stock`, {
      method: 'PUT',
      body: JSON.stringify({ in_stock: inStock })
    });
  }

  // Coupon endpoints
  async getCoupons(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/coupons/admin/list${queryString ? `?${queryString}` : ''}`);
  }

  async createCoupon(coupon) {
    return this.request('/coupons/admin/create', {
      method: 'POST',
      body: JSON.stringify(coupon)
    });
  }

  async updateCoupon(couponId, updates) {
    return this.request(`/coupons/admin/${couponId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async deleteCoupon(couponId) {
    return this.request(`/coupons/admin/${couponId}`, {
      method: 'DELETE'
    });
  }

  async toggleCoupon(couponId) {
    return this.request(`/coupons/admin/${couponId}/toggle`, {
      method: 'POST'
    });
  }

  async applyCoupon(code, cartTotal, category = null) {
    return this.request('/coupons/apply', {
      method: 'POST',
      body: JSON.stringify({ code, cart_total: cartTotal, category })
    });
  }

  // Product ordering
  async getHomepageProducts() {
    return this.request('/admin/products/homepage');
  }

  async reorderProducts(productOrders) {
    return this.request('/admin/products/reorder', {
      method: 'PUT',
      body: JSON.stringify(productOrders)
    });
  }

  // Image upload
  async uploadImage(formData) {
    const url = `${this.baseUrl}/uploads/image`;
    const headers = {};
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload image');
    }
    
    return response.json();
  }

  // Checkout endpoints
  async createCheckoutSession(originUrl, shippingAddress = null, loyaltyPointsToUse = 0, giftCardCode = null) {
    const body = { 
      origin_url: originUrl,
      loyalty_points_to_use: loyaltyPointsToUse,
      gift_card_code: giftCardCode
    };
    if (shippingAddress) {
      body.shipping_address = shippingAddress;
    }
    return this.request('/checkout/create-session', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/checkout/status/${sessionId}`);
  }

  async getUserOrders() {
    return this.request('/checkout/orders');
  }

  async getOrderDetails(orderId) {
    return this.request(`/checkout/orders/${orderId}`);
  }

  // Admin Order Management endpoints
  async getAdminOrderStats() {
    return this.request('/admin/orders/stats');
  }

  async getAdminOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/orders${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminOrderDetails(orderId) {
    return this.request(`/admin/orders/${orderId}`);
  }

  async updateAdminOrderStatus(orderId, statusData) {
    return this.request(`/admin/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData)
    });
  }

  async cancelAdminOrder(orderId) {
    return this.request(`/admin/orders/${orderId}`, {
      method: 'DELETE'
    });
  }

  // New Order Management endpoints
  async getAdminOrdersList(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/orders/admin/list${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminOrderDetail(orderId) {
    return this.request(`/orders/admin/${orderId}`);
  }

  async getOrdersStats() {
    return this.request('/orders/admin/stats');
  }

  async addOrUpdateTracking(orderId, shippingData) {
    return this.request(`/orders/admin/${orderId}/shipping`, {
      method: 'POST',
      body: JSON.stringify(shippingData)
    });
  }

  async markOrderShipped(orderId, shippingData) {
    return this.request(`/orders/admin/${orderId}/mark-shipped`, {
      method: 'POST',
      body: JSON.stringify(shippingData)
    });
  }

  async markOrderDelivered(orderId) {
    return this.request(`/orders/admin/${orderId}/mark-delivered`, {
      method: 'POST'
    });
  }

  async removeOrderTracking(orderId) {
    return this.request(`/orders/admin/${orderId}/tracking`, {
      method: 'DELETE'
    });
  }

  async resendOrderEmail(orderId, emailType) {
    return this.request(`/orders/admin/${orderId}/resend-email/${emailType}`, {
      method: 'POST'
    });
  }

  async cancelOrderAdmin(orderId) {
    return this.request(`/orders/admin/${orderId}/cancel`, {
      method: 'POST'
    });
  }

  async processRefund(orderId, refundData) {
    return this.request(`/orders/admin/${orderId}/refund`, {
      method: 'POST',
      body: JSON.stringify(refundData)
    });
  }

  // Public order tracking (no auth required)
  async getOrderByTrackingToken(token) {
    return this.request(`/orders/track/${token}`);
  }

  async verifyAdminPassword(password) {
    return this.request('/admin/verify-password', {
      method: 'POST',
      body: JSON.stringify({ password })
    });
  }

  // Address Management endpoints
  async getAddresses() {
    return this.request('/addresses/');
  }

  async createAddress(addressData) {
    return this.request('/addresses/', {
      method: 'POST',
      body: JSON.stringify(addressData)
    });
  }

  async updateAddress(addressId, addressData) {
    return this.request(`/addresses/${addressId}`, {
      method: 'PUT',
      body: JSON.stringify(addressData)
    });
  }

  async deleteAddress(addressId) {
    return this.request(`/addresses/${addressId}`, {
      method: 'DELETE'
    });
  }

  async setDefaultAddress(addressId) {
    return this.request(`/addresses/${addressId}/set-default`, {
      method: 'POST'
    });
  }

  // Blog API methods
  async getBlogPosts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/blog/posts${queryString ? `?${queryString}` : ''}`);
  }

  async getBlogPost(slug) {
    return this.request(`/blog/posts/${slug}`);
  }

  async getBlogCategories() {
    return this.request('/blog/categories');
  }

  async getBlogTags() {
    return this.request('/blog/tags');
  }

  // Blog Admin methods
  async getAdminBlogPosts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/blog/admin/posts${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminBlogPost(postId) {
    return this.request(`/blog/admin/posts/${postId}`);
  }

  async createBlogPost(postData) {
    return this.request('/blog/admin/posts', {
      method: 'POST',
      body: JSON.stringify(postData)
    });
  }

  async updateBlogPost(postId, postData) {
    return this.request(`/blog/admin/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(postData)
    });
  }

  async deleteBlogPost(postId) {
    return this.request(`/blog/admin/posts/${postId}`, {
      method: 'DELETE'
    });
  }

  async publishBlogPost(postId) {
    return this.request(`/blog/admin/posts/${postId}/publish`, {
      method: 'POST'
    });
  }

  async unpublishBlogPost(postId) {
    return this.request(`/blog/admin/posts/${postId}/unpublish`, {
      method: 'POST'
    });
  }

  async migrateBlogData() {
    return this.request('/blog/admin/migrate', {
      method: 'POST'
    });
  }

  // ==================== INVENTORY API ====================
  
  async getInventoryStats() {
    return this.request('/inventory/admin/stats');
  }

  async getLowStockProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/inventory/admin/low-stock?${queryString}`);
  }

  async getOutOfStockProducts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/inventory/admin/out-of-stock?${queryString}`);
  }

  async updateProductStock(productId, data) {
    return this.request(`/inventory/admin/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async checkStockAvailability(productId, quantity = 1) {
    return this.request(`/inventory/check/${productId}?quantity=${quantity}`);
  }

  // ==================== BUNDLES API ====================
  
  async getBundles(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/bundles/?${queryString}`);
  }

  async getBundle(bundleId) {
    return this.request(`/bundles/${bundleId}`);
  }

  async checkBundleStock(bundleId, quantity = 1) {
    return this.request(`/bundles/${bundleId}/stock?quantity=${quantity}`);
  }

  async getBundleStockHealth(severity = 'all') {
    return this.request(`/bundles/admin/stock-health?severity=${severity}`);
  }

  async getAdminBundles(params = {}) {
    // Filter out undefined values
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    const queryString = new URLSearchParams(filteredParams).toString();
    return this.request(`/bundles/admin/list?${queryString}`);
  }

  async createBundle(data) {
    return this.request('/bundles/admin', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateBundle(bundleId, data) {
    return this.request(`/bundles/admin/${bundleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteBundle(bundleId) {
    return this.request(`/bundles/admin/${bundleId}`, {
      method: 'DELETE'
    });
  }

  async toggleBundle(bundleId) {
    return this.request(`/bundles/admin/${bundleId}/toggle`, {
      method: 'POST'
    });
  }

  // ==================== MENU CATEGORIES API ====================
  
  async getMenuCategories() {
    return this.request('/menu/categories');
  }

  async getAdminMenuCategories() {
    return this.request('/menu/admin/categories');
  }

  async createMenuCategory(data) {
    return this.request('/menu/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateMenuCategory(categoryId, data) {
    return this.request(`/menu/admin/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteMenuCategory(categoryId) {
    return this.request(`/menu/admin/categories/${categoryId}`, {
      method: 'DELETE'
    });
  }

  async reorderMenuCategories(categoryOrders) {
    return this.request('/menu/admin/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ category_orders: categoryOrders })
    });
  }

  async seedDefaultMenuCategories() {
    return this.request('/menu/admin/categories/seed-defaults', {
      method: 'POST'
    });
  }

  // ==================== LOYALTY POINTS API ====================
  
  async getLoyaltyBalance() {
    return this.request('/loyalty/balance');
  }

  async getLoyaltyHistory(page = 1) {
    return this.request(`/loyalty/history?page=${page}`);
  }

  async redeemLoyaltyPoints(points) {
    return this.request('/loyalty/redeem', {
      method: 'POST',
      body: JSON.stringify({ points })
    });
  }

  async cancelLoyaltyRedemption(redemptionId) {
    return this.request(`/loyalty/cancel-redemption/${redemptionId}`, {
      method: 'POST'
    });
  }

  // ==================== GIFT CARDS API ====================
  
  async getGiftCardOptions() {
    return this.request('/gift-cards/options');
  }

  async purchaseGiftCard(data) {
    return this.request('/gift-cards/purchase', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async checkGiftCard(code) {
    return this.request(`/gift-cards/check/${encodeURIComponent(code)}`);
  }

  async redeemGiftCard(code, amount) {
    return this.request('/gift-cards/redeem', {
      method: 'POST',
      body: JSON.stringify({ code, amount })
    });
  }

  async getMyGiftCards() {
    return this.request('/gift-cards/my-cards');
  }

  // ==================== CURRENCY API ====================
  
  async getExchangeRates() {
    return this.request('/currency/rates');
  }

  async convertPrice(amount, fromCurrency, toCurrency) {
    return this.request('/currency/convert', {
      method: 'POST',
      body: JSON.stringify({
        amount,
        from_currency: fromCurrency,
        to_currency: toCurrency
      })
    });
  }

  async getCurrencySymbols() {
    return this.request('/currency/symbols');
  }

  // ==================== CATEGORIES API ====================
  
  async getCategories(includeInactive = false) {
    return this.request(`/categories?include_inactive=${includeInactive}`);
  }

  async getCategory(slug) {
    return this.request(`/categories/${slug}`);
  }

  async createCategory(data) {
    return this.request('/categories/admin', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateCategory(categoryId, data) {
    return this.request(`/categories/admin/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteCategory(categoryId) {
    return this.request(`/categories/admin/${categoryId}`, {
      method: 'DELETE'
    });
  }

  async reorderCategories(items) {
    return this.request('/categories/admin/reorder', {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  }

  async createSubcategory(categoryId, data) {
    return this.request(`/categories/admin/${categoryId}/subcategories`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateSubcategory(subcategoryId, data) {
    return this.request(`/categories/admin/subcategories/${subcategoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSubcategory(subcategoryId) {
    return this.request(`/categories/admin/subcategories/${subcategoryId}`, {
      method: 'DELETE'
    });
  }

  async reorderSubcategories(categoryId, items) {
    return this.request(`/categories/admin/${categoryId}/subcategories/reorder`, {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  }

  async seedDefaultCategories() {
    return this.request('/categories/admin/seed-defaults', {
      method: 'POST'
    });
  }

  // ==================== CONCERNS (Shop by Concern) API ====================
  
  async getConcerns(includeInactive = false, categoryId = null, categorySlug = null) {
    let url = `/concerns?include_inactive=${includeInactive}`;
    if (categoryId) url += `&category_id=${categoryId}`;
    if (categorySlug) url += `&category_slug=${categorySlug}`;
    return this.request(url);
  }

  async getConcern(slug) {
    return this.request(`/concerns/${slug}`);
  }

  async getConcernProducts(slug, page = 1, perPage = 12) {
    return this.request(`/concerns/${slug}/products?page=${page}&per_page=${perPage}`);
  }

  async createConcern(data) {
    return this.request('/concerns/admin', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateConcern(concernId, data) {
    return this.request(`/concerns/admin/${concernId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteConcern(concernId) {
    return this.request(`/concerns/admin/${concernId}`, {
      method: 'DELETE'
    });
  }

  async reorderConcerns(items) {
    return this.request('/concerns/admin/reorder', {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  }

  async seedDefaultConcerns() {
    return this.request('/concerns/admin/seed-defaults', {
      method: 'POST'
    });
  }

  async seedDefaultConcernsForCategory(categoryId) {
    return this.request(`/concerns/admin/seed-defaults/${categoryId}`, {
      method: 'POST'
    });
  }

  // ==================== PAYPAL API ====================
  
  async getPayPalConfig() {
    return this.request('/paypal/config');
  }

  async createPayPalOrder(orderData) {
    return this.request('/paypal/create-order', {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }

  async capturePayPalOrder(paypalOrderId, payerId) {
    return this.request('/paypal/capture-order', {
      method: 'POST',
      body: JSON.stringify({ paypal_order_id: paypalOrderId, payer_id: payerId })
    });
  }

  async cancelPayPalOrder(paypalOrderId) {
    return this.request(`/paypal/cancel-order/${paypalOrderId}`, {
      method: 'POST'
    });
  }

  async subscribeNewsletter(email) {
    // Try a few common endpoint shapes — if none exist this fails gracefully
    try {
      return await this.request('/newsletter/subscribe', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    } catch (e) {
      // Fallback to a generic contact-style endpoint if present
      return this.request('/contact/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    }
  }

  // ─── Site Images CMS ─────────────────────────────────────────────
  async getSiteImages() {
    return this.request('/site-images');
  }
  async getAdminSiteImages() {
    return this.request('/admin/site-images');
  }
  async setSiteImage(key, url) {
    return this.request(`/admin/site-images/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ url })
    });
  }
  async restoreSiteImage(key) {
    return this.request(`/admin/site-images/${encodeURIComponent(key)}`, {
      method: 'DELETE'
    });
  }
  async uploadImageFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.baseUrl}/uploads/image`;
    const res = await fetch(url, {
      method: 'POST',
      headers: this.authToken ? { Authorization: `Bearer ${this.authToken}` } : {},
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return res.json();
  }
}

const api = new ApiService();
export default api;
